import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { INDIAN_STATES } from "@/lib/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Search, FileText, Loader2, Truck } from "lucide-react";
import { toast } from "sonner";

const formatINR = (n) => {
  const v = Number(n || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}₹${Math.abs(v).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
};

const emptyForm = () => ({ name: "", gstin: "", state: "", email: "", phone: "", address: "" });

export default function VendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  // Statement modal
  const [statementVendor, setStatementVendor] = useState(null);
  const [statementForm, setStatementForm] = useState({ from_date: "", to_date: "" });
  const [downloadingStatement, setDownloadingStatement] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/vendors"),
      api.get("/vendors-balances").catch(() => ({ data: {} })),
    ])
      .then(([vr, br]) => {
        setVendors(vr.data || []);
        setBalances(br.data || {});
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!search) return vendors;
    const q = search.toLowerCase();
    return vendors.filter((v) =>
      (v.name || "").toLowerCase().includes(q) ||
      (v.gstin || "").toLowerCase().includes(q) ||
      (v.state || "").toLowerCase().includes(q)
    );
  }, [vendors, search]);

  const openCreate = () => { setEditId(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (v) => {
    setEditId(v.vendor_id);
    setForm({ name: v.name || "", gstin: v.gstin || "", state: v.state || "", email: v.email || "", phone: v.phone || "", address: v.address || "" });
    setDialogOpen(true);
  };

  const handleChange = (k, val) => setForm({ ...form, [k]: val });

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Vendor name is required");
    setSaving(true);
    try {
      if (editId) {
        const r = await api.put(`/vendors/${editId}`, form);
        setVendors(vendors.map((v) => (v.vendor_id === editId ? r.data : v)));
        toast.success("Vendor updated");
      } else {
        const r = await api.post("/vendors", form);
        setVendors([r.data, ...vendors.filter((v) => v.vendor_id !== r.data.vendor_id)]);
        toast.success("Vendor added");
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const deleteVendor = async (id) => {
    if (!window.confirm("Delete this vendor?")) return;
    try {
      await api.delete(`/vendors/${id}`);
      setVendors(vendors.filter((v) => v.vendor_id !== id));
      toast.success("Vendor deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cannot delete");
    }
  };

  const openStatement = (vendor) => {
    const today = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 90);
    setStatementForm({
      from_date: from.toISOString().split("T")[0],
      to_date: today.toISOString().split("T")[0],
    });
    setStatementVendor(vendor);
  };

  const downloadStatement = async () => {
    if (!statementVendor) return;
    setDownloadingStatement(true);
    try {
      const params = new URLSearchParams();
      if (statementForm.from_date) params.set("from_date", statementForm.from_date);
      if (statementForm.to_date) params.set("to_date", statementForm.to_date);
      const url = `/vendors/${statementVendor.vendor_id}/statement.pdf?${params.toString()}`;
      const r = await api.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `vendor_statement_${(statementVendor.name || "vendor").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Statement downloaded");
      setStatementVendor(null);
    } catch {
      toast.error("Failed to generate statement");
    } finally { setDownloadingStatement(false); }
  };

  if (loading) return <div className="p-12 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4338ca]" /></div>;

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="vendors-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0f172a] tracking-tight">Vendors</h1>
          <p className="text-sm text-[#64748b] mt-1">{vendors.length} suppliers · Outstanding amounts payable</p>
        </div>
        <Button onClick={openCreate} data-testid="add-vendor-btn" className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl shadow-sm">
          <Plus className="h-4 w-4 mr-2" /> Add Vendor
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94a3b8]" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, GSTIN, state..." data-testid="vendors-search-input"
          className="pl-9 h-10 border-[#e2e8f0]" />
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">{editId ? "Edit Vendor" : "Add Vendor"}</DialogTitle>
            <DialogDescription className="text-sm text-[#64748b]">Supplier details for purchases and payments tracking.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-sm font-medium">Name *</Label>
              <Input value={form.name} onChange={(e) => handleChange("name", e.target.value)} data-testid="vendor-name-input" className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">GSTIN</Label>
                <Input value={form.gstin} onChange={(e) => handleChange("gstin", e.target.value.toUpperCase())} placeholder="22AAAAA0000A1Z5"
                  data-testid="vendor-gstin-input" className="mt-1.5 font-mono uppercase" />
              </div>
              <div>
                <Label className="text-sm font-medium">State</Label>
                <Select value={form.state || "none"} onValueChange={(v) => handleChange("state", v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5" data-testid="vendor-state-select"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    {INDIAN_STATES.map((s) => <SelectItem key={s.code} value={s.code}>{s.code} - {s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Email</Label>
                <Input value={form.email} onChange={(e) => handleChange("email", e.target.value)} data-testid="vendor-email-input" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-sm font-medium">Phone</Label>
                <Input value={form.phone} onChange={(e) => handleChange("phone", e.target.value)} data-testid="vendor-phone-input" className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Address</Label>
              <Input value={form.address} onChange={(e) => handleChange("address", e.target.value)} data-testid="vendor-address-input" className="mt-1.5" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving} className="rounded-lg border-[#e2e8f0]">Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#4338ca] hover:bg-[#3730a3] rounded-lg" data-testid="save-vendor-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editId ? "Update" : "Add Vendor"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="p-16 text-center">
              <Truck className="h-12 w-12 text-[#e2e8f0] mx-auto mb-3" />
              <p className="text-sm font-medium text-[#0f172a] mb-1">{vendors.length === 0 ? "No vendors yet" : "No matches"}</p>
              <p className="text-xs text-[#64748b] mb-4">{vendors.length === 0 ? "Vendors auto-appear when you record purchases. Or add one manually." : "Try a different search."}</p>
              {vendors.length === 0 && <Button onClick={openCreate} className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl"><Plus className="h-4 w-4 mr-2" /> Add Vendor</Button>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Name</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">GSTIN</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">State</th>
                    <th className="text-right py-3 px-4 text-[#64748b] font-medium">Outstanding Payable</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Email</th>
                    <th className="text-right py-3 px-4 text-[#64748b] font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((v) => {
                    const bal = balances[v.vendor_id] || { outstanding: 0, advance_balance: 0, net_balance: 0 };
                    const net = bal.net_balance;
                    const isAdvance = net < -0.01;
                    const hasDue = net > 0.01;
                    return (
                      <tr key={v.vendor_id} className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc]" data-testid={`vendor-row-${v.vendor_id}`}>
                        <td className="py-3 px-4 text-[#0f172a] font-medium">{v.name}</td>
                        <td className="py-3 px-4 text-[#64748b] font-mono text-xs">{v.gstin || "-"}</td>
                        <td className="py-3 px-4 text-[#64748b]">{v.state || "-"}</td>
                        <td className="py-3 px-4 text-right" data-testid={`vendor-balance-${v.vendor_id}`}>
                          {hasDue ? (
                            <span className="font-mono text-sm font-semibold text-[#ef4444]" title="We owe this">{formatINR(net)}</span>
                          ) : isAdvance ? (
                            <span className="font-mono text-sm font-semibold text-[#10b981]" title="Advance with vendor">{formatINR(net)}</span>
                          ) : (
                            <span className="font-mono text-sm text-[#94a3b8]">{formatINR(0)}</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-[#64748b]">{v.email || "-"}</td>
                        <td className="py-3 px-4 text-right">
                          <Button size="sm" variant="ghost" onClick={() => openStatement(v)} title="Download statement" data-testid={`statement-vendor-${v.vendor_id}`}>
                            <FileText className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(v)} data-testid={`edit-vendor-${v.vendor_id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteVendor(v.vendor_id)} className="text-red-500" data-testid={`delete-vendor-${v.vendor_id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statement of Account dialog */}
      <Dialog open={!!statementVendor} onOpenChange={(open) => !open && !downloadingStatement && setStatementVendor(null)}>
        <DialogContent className="max-w-md" data-testid="vendor-statement-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-lg flex items-center gap-2">
              <FileText className="h-4 w-4 text-[#4338ca]" /> Vendor Statement
            </DialogTitle>
            <DialogDescription>Chronological ledger PDF — purchases vs payments with running payable balance.</DialogDescription>
          </DialogHeader>
          {statementVendor && (
            <div className="space-y-4 mt-2">
              <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-lg p-3">
                <p className="text-xs text-[#64748b]">Vendor</p>
                <p className="font-outfit font-semibold text-[#0f172a]">{statementVendor.name}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium text-[#64748b]">From date</Label>
                  <Input type="date" value={statementForm.from_date} onChange={(e) => setStatementForm({ ...statementForm, from_date: e.target.value })}
                    data-testid="vendor-statement-from" className="mt-1 border-[#e2e8f0]" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-[#64748b]">To date</Label>
                  <Input type="date" value={statementForm.to_date} onChange={(e) => setStatementForm({ ...statementForm, to_date: e.target.value })}
                    data-testid="vendor-statement-to" className="mt-1 border-[#e2e8f0]" />
                </div>
              </div>
              <p className="text-[11px] text-[#94a3b8] italic">Leave dates empty to include all transactions.</p>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setStatementVendor(null)} disabled={downloadingStatement} className="rounded-lg border-[#e2e8f0]">Cancel</Button>
                <Button onClick={downloadStatement} disabled={downloadingStatement} className="bg-[#4338ca] hover:bg-[#3730a3] text-white rounded-lg" data-testid="download-vendor-statement-btn">
                  {downloadingStatement ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                  Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
